import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useSocket — Custom hook for Socket.io real-time connection.
 * 
 * When socket.io-client is available, connects to the server and
 * listens for 'amr:alert' and 'amr:data-update' events.
 * 
 * Falls back gracefully if socket.io-client is not installed —
 * returns disconnected state with empty data.
 */

let io = null;
try {
    io = require('socket.io-client');
} catch (e) {
    console.warn('socket.io-client not available — realtime features disabled');
}

export default function useSocket(serverUrl) {
    const [connected, setConnected] = useState(false);
    const [alerts, setAlerts] = useState([]);
    const [latestData, setLatestData] = useState(null);
    const socketRef = useRef(null);
    const alertIdRef = useRef(0);

    useEffect(() => {
        if (!io) return;

        const socket = io(serverUrl || window.location.origin, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: 2000,
        });

        socketRef.current = socket;
        window.__amrSocket = socket;

        socket.on('connect', () => {
            console.log('🔌 Socket.io connected');
            setConnected(true);
        });

        socket.on('disconnect', () => {
            console.log('🔌 Socket.io disconnected');
            setConnected(false);
        });

        socket.on('amr:alert', (data) => {
            const alert = {
                ...data,
                id: `alert_${++alertIdRef.current}`,
                timestamp: new Date().toISOString(),
            };
            setAlerts(prev => [...prev, alert]);
        });

        socket.on('amr:data-update', (data) => {
            setLatestData(data);
        });

        return () => {
            socket.disconnect();
        };
    }, [serverUrl]);

    const dismissAlert = useCallback((alertId) => {
        setAlerts(prev => prev.filter(a => a.id !== alertId));
    }, []);

    return {
        connected,
        alerts,
        latestData,
        dismissAlert,
        socket: socketRef.current,
    };
}
